import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import { useI18n } from "@/i18n/LanguageContext";

export default function Garage() {
  const { t } = useI18n();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    api
      .get("/vehicles")
      .then((r) => setVehicles(r.data || []))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, []);

  const filters = [
    { key: "all", label: t("garage.filter.all") },
    { key: "available", label: t("garage.filter.available") },
    { key: "reserved", label: t("garage.filter.reserved") },
    { key: "sold", label: t("garage.filter.sold") },
  ];

  const filtered = useMemo(() => {
    if (filter === "all") return vehicles;
    return vehicles.filter((v) => v.status === filter);
  }, [vehicles, filter]);

  return (
    <div
      data-testid="garage-page"
      className="max-w-7xl mx-auto px-6 md:px-10 pt-[110px] md:pt-[128px] pb-24"
    >
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="section-label mb-4">{t("garage.label")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
            {t("garage.title")}
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl text-sm leading-relaxed">
            {t("garage.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="garage-filters">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-[10px] tracking-[0.3em] uppercase px-4 py-2.5 border transition-colors ${
                filter === f.key
                  ? "border-[#C6A15B] text-[#C6A15B] bg-[#C6A15B]/5"
                  : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-[4/3] bg-[#121212] border border-white/5 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-24 text-center">
          <div className="font-display text-2xl text-zinc-400">{t("garage.empty.title")}</div>
          <p className="text-sm text-zinc-500 mt-2">{t("garage.empty.subtitle")}</p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="garage-grid"
        >
          {filtered.map((v) => (
            <VehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
      )}
    </div>
  );
}
